import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiDataService } from '@core/services/api/api-data.service';

/**
 * Mapa `język => tekst`. Konfiguracja edycji przychodzi z backendu w takiej postaci,
 * bo jedna praca ma jeden zestaw edycji obsługujący wszystkie języki strony — inaczej
 * niż blok strony, który backend odsyła już rozstrzygnięty dla jednego języka.
 */
export type LocaleMap = Record<string, string>;

export interface EditionChoice {
  id: number;
  name: LocaleMap;
  price: number;
  availableQuantity: number;
  isDefault: boolean;
  sortOrder: number;
}

export interface EditionOption {
  id: number;
  name: LocaleMap;
  surcharge: number;
  isDefault: boolean;
  sortOrder: number;
}

export interface EditionOptionGroup {
  id: number;
  label: LocaleMap;
  sortOrder: number;
  options: EditionOption[];
}

/**
 * Pojedynczy wymiar w kształcie, w jakim niesie go publiczna konfiguracja cenowa —
 * kanoniczny [x, y, z, type, unit], ten sam co wymiary pracy i edycji w reszcie API.
 */
export interface SelectionDimension {
  x: number;
  y: number;
  z: number;
  type: number;
  unit: string;
}

/**
 * Wyceniony wybór kupującego. `variantId` to jednostka handlowa materializowana przez
 * backend leniwie — pojawia się dopiero, gdy ktoś o dany zestaw edycji i opcji zapyta.
 * `dimensions` to wymiary WYBRANEJ edycji (z fallbackiem na pracę) — zasilają reaktywny
 * snippet „Wymiary", który konfigurator odświeża po zmianie nakładu.
 */
export interface PricedSelection {
  editionId: number | null;
  optionIds: number[];
  variantId: number | null;
  price: number;
  availableQuantity: number;
  // Opcjonalne w typie: backend niesie je zawsze, ale starsza odpowiedź z cache może ich nie mieć,
  // a formatowanie i tak radzi sobie z brakiem (pusty snippet zamiast błędu).
  dimensions?: SelectionDimension[];
}

export interface PriceConfiguration {
  hasEditions: boolean;
  /**
   * Czy STATUS pracy pozwala pokazać przycisk zakupu — osobno od stanu magazynowego.
   *
   * Kanoniczna brama sklepu skleja oba powody w jeden boolean, a mają różny skutek:
   * brak zgody statusu CHOWA przycisk, brak sztuk go wyszarza. Pole jest opcjonalne
   * wyłącznie na czas rozjazdu wdrożeń — brak wartości traktujemy jak zgodę, bo
   * schowanie wezwania do zakupu przez nieobecne pole byłoby gorsze niż pokazanie go.
   */
  statusAllowsBuyButton?: boolean;
  currency: string;
  mainParameterLabel: LocaleMap;
  editions: EditionChoice[];
  optionGroups: EditionOptionGroup[];
  selection: PricedSelection;
}

/**
 * Transport konfiguracji cenowej pracy dla karty produktu.
 *
 * Serwis wyłącznie przenosi dane — nie ma tu ani jednego działania arytmetycznego na kwotach.
 * Cena wariantu jest wyliczana po stronie backendu z ceny edycji i dopłat wybranych opcji;
 * powtórzenie tego rachunku w przeglądarce zduplikowałoby denormalizację i przy pierwszej
 * rozbieżności zasad (zaokrąglenia, przelicznik waluty, dopłaty warunkowe) sklep pokazywałby
 * kwotę inną niż ta, którą zamówienie faktycznie policzy.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiEditionConfigurationService extends ApiDataService {
  apiUrl = 'client/inventory_works';

  /**
   * Pełna konfiguracja pracy wraz z wyceną wyboru domyślnego. Dla pracy bez edycji
   * odpowiedź niesie `hasEditions: false`, puste listy i wycenę samej pracy — kartę
   * obsługuje wtedy ten sam komponent, tylko bez list rozwijanych.
   */
  configurationFor(workId: number): Observable<PriceConfiguration> {
    return this.httpClient.get<PriceConfiguration>(this.buildUrl(workId, null, []));
  }

  /**
   * Wycena wskazanego wyboru. Odpowiedź ma ten sam kształt co konfiguracja, ale
   * wołającego interesuje wyłącznie część `selection` — listy edycji i grup opcji
   * się nie zmieniają, więc komponent nie podmienia ich przy każdym kliknięciu.
   */
  priceFor(workId: number, editionId: number | null, optionIds: number[]): Observable<PricedSelection> {
    return this.httpClient.get<PriceConfiguration>(this.buildUrl(workId, editionId, optionIds)).pipe(
      map(konfiguracja => konfiguracja.selection),
    );
  }

  private buildUrl(workId: number, editionId: number | null, optionIds: number[]): string {
    const params = new URLSearchParams();

    if (editionId !== null) {
      params.set('editionId', String(editionId));
    }

    optionIds.forEach(optionId => params.append('optionIds[]', String(optionId)));

    const url = `${this.apiUrlService.getApiUrl()}${this.apiUrl}/${workId}/price-configuration`;
    const query = this.currencyPreferenceService.withCurrencyParam(params).toString();

    return `${url}?${query}`;
  }
}
